/**
 * Trace reads (`audit.tracing.manage`): the Audit Log head's button and its dialog, which turns read tracing on for a
 * while (`PUT /api/audit-log/tracing`). The duration choices stop at the server's `max_hours` (and offer it when it
 * is not one of them); the person is optional (none traces everyone) and found by the people lookup; the reason is
 * required. A refusal stays in the dialog: "Already on: stop it first." for a 409, the server's reason otherwise.
 */

import { useState } from 'react';
import { Eye, Search, UserPlus, X } from 'lucide-react';

import { lookupUsersApiUsersLookupGetParamsQMin, TracingIn, tracingInReasonMax } from '@/api/generated/schemas';
import { ApiError } from '@/api/client';
import { useAuditTracing, useStartTracing, useUserLookup } from '@/api/queries';
import type { UserLookupRow } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { useSession } from '@/session/SessionContext';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { CharCount } from '@/ui/CharCount';
import { FieldError, SaveBlockedNote, fieldA11y } from '@/ui/FieldError';
import { FieldLabel } from '@/ui/FieldLabel';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';
import { useDebouncedSearch } from '@/ui/useDebouncedSearch';

const DURATIONS = [1, 4, 8, 24, 72, 168];

/** The durations offered: the usual ones up to `max`, then `max` itself when it is not one of them. */
export function durationOptions(max: number): number[] {
  const usual = DURATIONS.filter((h) => h <= max);
  return max >= 1 && !usual.includes(max) ? [...usual, max] : usual;
}

export function TraceReadsButton() {
  const [open, setOpen] = useState(false);
  const tracing = useAuditTracing();
  return (
    <>
      <button type="button" className="btn btn-ghost" disabled={!tracing.data} onClick={() => setOpen(true)}>
        <Eye aria-hidden="true" /> Trace reads
      </button>
      {open && tracing.data && <TraceReadsDialog maxHours={tracing.data.max_hours} onClose={() => setOpen(false)} />}
    </>
  );
}

function refusal(error: unknown): unknown {
  if (error instanceof ApiError && error.status === 409) return 'Already on: stop it first.';
  return bannerError(error, ['hours', 'reason', 'user_id']);
}

function DurationField({ options, hours, onChange }: { options: number[]; hours: number; onChange: (hours: number) => void }) {
  return (
    <div className="field">
      <span className="label" id="trace-hours-label">For</span>
      <div className="segmented" role="radiogroup" aria-labelledby="trace-hours-label">
        {options.map((h) => (
          <button key={h} type="button" role="radio" aria-checked={hours === h} onClick={() => onChange(h)}>
            {h} h
          </button>
        ))}
      </div>
    </div>
  );
}

function PersonResults({ q, onPick }: { q: string; onPick: (row: UserLookupRow) => void }) {
  const lookup = useUserLookup(q);
  if (q.length < lookupUsersApiUsersLookupGetParamsQMin) return null;
  if (lookup.isPending) return <p className="hint" role="status">Searching people</p>;
  if (lookup.error) return <p className="hint" role="status">The search failed. Try again.</p>;
  if (lookup.data.users.length === 0) return <p className="hint" role="status">No one matches. Check the spelling.</p>;
  return (
    <ul className="expert-results" aria-label="People found">
      {lookup.data.users.map((u) => (
        <li key={u.id}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onPick(u)}>
            <UserPlus aria-hidden="true" /> {u.display_name} <span className="hint">{u.email}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** The one person whose reads to trace, or everyone's; the people lookup needs `asset.manage`. */
function PersonField({ person, error, onChange }: { person: UserLookupRow | null; error?: string; onChange: (person: UserLookupRow | null) => void }) {
  const { can } = useSession();
  const [q, setQ] = useState('');
  const [text, setText] = useDebouncedSearch(q, (patch) => setQ(patch.q));
  const searchable = can('asset.manage');
  return (
    <fieldset className="field-group">
      <legend>Whose reads</legend>
      {person ? (
        <div className="expert-row">
          <span className="expert-who">
            <b>{person.display_name}</b> <span className="hint">{person.email}</span>
          </span>
          <button type="button" className="icon-btn" aria-label={`Trace everyone, not only ${person.display_name}`} title="Trace everyone" onClick={() => onChange(null)}>
            <X aria-hidden="true" />
          </button>
        </div>
      ) : (
        <p>Everyone{searchable ? ', unless you pick one person.' : '.'}</p>
      )}
      {searchable && !person && (
        <>
          <div className="search-field">
            <Search aria-hidden="true" />
            <input
              id="trace-person"
              type="search"
              value={text}
              placeholder="Find a person by name or e-mail"
              aria-label="Find a person to trace"
              {...fieldA11y('trace-person', error)}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <div aria-live="polite">
            <PersonResults
              q={q}
              onPick={(row) => {
                onChange(row);
                setText('');
              }}
            />
          </div>
        </>
      )}
      <FieldError inputId="trace-person" message={error} />
    </fieldset>
  );
}

export function TraceReadsDialog({ maxHours, onClose }: { maxHours: number; onClose: () => void }) {
  const start = useStartTracing();
  const options = durationOptions(maxHours);
  const [hours, setHours] = useState(options[0] ?? 1);
  const [person, setPerson] = useState<UserLookupRow | null>(null);
  const [reason, setReason] = useState('');
  const body = { hours, user_id: person?.id ?? null, reason: reason.trim() };
  const invalid = Object.keys(fieldErrors(TracingIn, body)).length > 0;
  const server = serverFieldErrors(start.error);

  return (
    <Modal
      title="Trace reads"
      subtitle="Records who opens which records, for a while. Everyone who reads the Audit Log sees that tracing is on."
      onClose={onClose}
      onSubmit={() => {
        if (!invalid) start.mutate(body, { onSuccess: onClose });
      }}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton
            state={buttonState(start)}
            label="Start tracing"
            pendingLabel="Starting…"
            doneLabel="Started"
            disabled={invalid}
            describedBy={invalid ? 'trace-blocked' : undefined}
          />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={refusal(start.error)} />
        <DurationField options={options} hours={hours} onChange={setHours} />
        <PersonField person={person} error={server.user_id} onChange={setPerson} />
        <div className="field">
          <FieldLabel htmlFor="trace-reason" label="Reason" />
          <textarea
            id="trace-reason"
            rows={3}
            required
            autoFocus
            maxLength={tracingInReasonMax}
            value={reason}
            {...fieldA11y('trace-reason', server.reason, 'trace-reason-count')}
            onChange={(e) => setReason(e.target.value)}
          />
          <FieldError inputId="trace-reason" message={server.reason} />
          <CharCount id="trace-reason-count" value={reason} max={tracingInReasonMax}>
            Every audit reader sees it.
          </CharCount>
        </div>
        <SaveBlockedNote id="trace-blocked" show={invalid}>Give a reason of at least 3 characters to start.</SaveBlockedNote>
      </div>
    </Modal>
  );
}
